import { create } from "zustand";
import { MapMarker, LassoMode } from "../components/map/map.types";

export interface NotificationRecipientsStore {
  addresses: MapMarker[];
  selectedAddresses: MapMarker[];
  mapCentering: MapMarker[];
  drawing: boolean;
  lassoMode: LassoMode;
  mapZoom: number;
  mapHeight: string;
  query: string;
  isLoading: boolean;
  isSearchLoading: boolean;
  searchedAddresses: MapMarker[];
  hasManualSelection: boolean;
  // Filter state
  showAllCustomers: boolean;
  showFlatRateOnly: boolean;
  showOnDemandOnly: boolean;
  showSnowReporters: boolean;
  visibleRegions: Set<string>;
}

export interface NotificationRecipientsActions {
  setAddresses: (addresses: MapMarker[], isInitialLoad?: boolean) => void;
  setSelectedAddress: (
    address: MapMarker[],
    multiSelect?: boolean,
    isInitialLoad?: boolean,
    isQuickSelect?: boolean,
  ) => void;
  resetAllSelected: () => void;
  resetStore: () => void;
  setMapHeight: (height: string) => void;
  toggleDrawing: (drawing: boolean, mode?: LassoMode) => void;
  setMapCentering: (id: string | undefined, addresses?: MapMarker[]) => void;
  clearMapCentering: () => void;
  setMapZoom: (zoom: number) => void;
  setQuery: (query: string) => void;
  removeSelected: (id: string) => void;
  setIsLoading: (loading: boolean) => void;
  setIsSearchLoading: (loading: boolean) => void;
  setSearchedAddresses: (
    addresses: MapMarker[],
    isInitialLoad?: boolean,
  ) => void;
  findAddress: (id: string) => MapMarker | undefined;
  resetHasManualSelection: () => void;
  // Filter actions
  setShowAllCustomers: (show: boolean) => void;
  setShowFlatRateOnly: (show: boolean) => void;
  setShowOnDemandOnly: (show: boolean) => void;
  setShowSnowReporters: (show: boolean) => void;
  toggleRegionVisibility: (regionId: string) => void;
  getFilteredAddresses: () => MapMarker[];
}

const initialState: NotificationRecipientsStore = {
  addresses: [],
  selectedAddresses: [],
  mapCentering: [],
  drawing: false,
  lassoMode: LassoMode.REPLACE,
  mapZoom: 13,
  mapHeight: "calc(100vh - 200px)",
  query: "",
  isLoading: false,
  isSearchLoading: false,
  searchedAddresses: [],
  hasManualSelection: false,
  // Default filter state - all filters ON (all customer types visible)
  showAllCustomers: true,
  showFlatRateOnly: true,
  showOnDemandOnly: true,
  showSnowReporters: true,
  visibleRegions: new Set<string>(),
};

const useNotificationRecipientsStore = create<
  NotificationRecipientsStore & NotificationRecipientsActions
>((setState, getState) => ({
  ...initialState,
  setAddresses: (addresses, isInitialLoad = false) =>
    setState((state) => {
      // Preserve selection state
      const selectedIds = new Set(
        state.selectedAddresses.map((addr) => addr.id),
      );

      const addressesWithSelectionState = addresses.map((address) => ({
        ...address,
        selected: selectedIds.has(address.id),
      }));

      // Update selectedAddresses with fresh data while preserving selection
      const updatedSelectedAddresses = state.selectedAddresses.map(
        (selectedAddr) => {
          const freshData = addresses.find(
            (addr) => addr.id === selectedAddr.id,
          );
          if (freshData) {
            return {
              ...freshData,
              selected: true,
            };
          }
          return selectedAddr;
        },
      );

      return {
        addresses: addressesWithSelectionState,
        selectedAddresses: updatedSelectedAddresses,
        // Clear map centering when addresses change (except for initial loads)
        mapCentering: isInitialLoad ? state.mapCentering : [],
      };
    }),
  setSelectedAddress: (
    selected,
    multiSelect = false,
    isInitialLoad = false,
    isQuickSelect = false,
  ) =>
    setState((state) => {
      // Set hasManualSelection to true only if this is not the initial load AND not from Quick select
      const newState = {
        hasManualSelection:
          isInitialLoad || isQuickSelect ? state.hasManualSelection : true,
        // Clear map centering when selection changes (except for initial loads)
        mapCentering: isInitialLoad ? state.mapCentering : [],
      };

      if (!multiSelect) {
        return {
          ...newState,
          selectedAddresses: selected.map((addr) => ({
            ...addr,
            selected: true,
          })),
          addresses: state.addresses.map((addr) => ({
            ...addr,
            selected: selected.some(
              (selectedAddr) => selectedAddr.id === addr.id,
            ),
          })),
        };
      } else {
        const existingSelectedIds = new Set(
          state.selectedAddresses.map((addr) => addr.id),
        );

        // Add new selections
        selected.forEach((addr) => {
          if (!existingSelectedIds.has(addr.id)) {
            existingSelectedIds.add(addr.id);
          }
        });

        const updatedSelectedAddresses = state.addresses.filter((addr) =>
          existingSelectedIds.has(addr.id),
        );

        return {
          ...newState,
          selectedAddresses: updatedSelectedAddresses.map((addr) => ({
            ...addr,
            selected: true,
          })),
          addresses: state.addresses.map((addr) => ({
            ...addr,
            selected: existingSelectedIds.has(addr.id),
          })),
        };
      }
    }),
  resetAllSelected: () =>
    setState((state) => ({
      selectedAddresses: [],
      addresses: state.addresses.map((addr) => ({ ...addr, selected: false })),
      // Clear map centering when resetting all selections
      mapCentering: [],
    })),
  resetStore: () =>
    setState(() => ({
      ...initialState,
      visibleRegions: new Set<string>(), // Ensure new Set instance
      mapCentering: [], // Clear map centering when resetting store
    })),
  setMapHeight: (height) =>
    setState({
      mapHeight: height,
      // Don't clear map centering when height changes to preserve user's current view
    }),
  toggleDrawing: (drawing, mode = LassoMode.REPLACE) =>
    setState({
      drawing,
      lassoMode: mode,
      // Don't clear map centering when drawing mode changes to preserve user's current view
    }),
  setMapCentering: (id, addresses) =>
    setState((state) => {
      let centeringAddresses: MapMarker[] = [];
      if (addresses) {
        centeringAddresses = addresses;
      }
      if (id) {
        const targetAddress = state.addresses.find((addr) => addr.id === id);
        centeringAddresses = targetAddress ? [targetAddress] : [];
      }
      return {
        mapCentering: centeringAddresses,
      };
    }),
  clearMapCentering: () => setState({ mapCentering: [] }),
  setMapZoom: (zoom) =>
    setState({
      mapZoom: zoom,
      // Don't clear map centering when zoom changes to preserve user's current view
    }),
  setQuery: (query) =>
    setState({
      query,
      // Don't clear map centering when search query changes to preserve user's current view
    }),
  removeSelected: (id) =>
    setState((state) => ({
      selectedAddresses: state.selectedAddresses.filter(
        (addr) => addr.id !== id,
      ),
      addresses: state.addresses.map((addr) =>
        addr.id === id ? { ...addr, selected: false } : addr,
      ),
      // Clear map centering when removing addresses to prevent unwanted zooming
      mapCentering: [],
    })),
  setIsLoading: (loading) =>
    setState({
      isLoading: loading,
      // Don't clear map centering when loading state changes to preserve user's current view
    }),
  setIsSearchLoading: (loading) =>
    setState({
      isSearchLoading: loading,
      // Don't clear map centering when search loading state changes to preserve user's current view
    }),
  setSearchedAddresses: (addresses, isInitialLoad = false) =>
    setState({
      searchedAddresses: addresses,
      // Don't clear map centering during search to preserve user's current view
      mapCentering: isInitialLoad ? getState().mapCentering : [],
    }),
  findAddress: (id) => getState().addresses.find((addr) => addr.id === id),
  resetHasManualSelection: () =>
    setState({
      hasManualSelection: false,
    }),
  // Filter actions
  setShowAllCustomers: (show) =>
    setState({
      showAllCustomers: show,
      // Don't clear map centering when filters change to preserve user's current view
    }),
  setShowFlatRateOnly: (show) =>
    setState({
      showFlatRateOnly: show,
      // Don't clear map centering when filters change to preserve user's current view
    }),
  setShowOnDemandOnly: (show) =>
    setState({
      showOnDemandOnly: show,
      // Don't clear map centering when filters change to preserve user's current view
    }),
  setShowSnowReporters: (show) =>
    setState({
      showSnowReporters: show,
      // Don't clear map centering when filters change to preserve user's current view
    }),
  toggleRegionVisibility: (regionId) =>
    setState((state) => {
      const newVisibleRegions = new Set(state.visibleRegions);

      if (newVisibleRegions.has(regionId)) {
        newVisibleRegions.delete(regionId);
      } else {
        newVisibleRegions.add(regionId);
      }

      return {
        visibleRegions: newVisibleRegions,
        // Don't clear map centering when filters change to preserve user's current view
      };
    }),
  getFilteredAddresses: () => {
    const state = getState();
    // This method is now handled in the component directly for better memoization
    return state.addresses;
  },
}));

export default useNotificationRecipientsStore;
